/**
 * Amazon Ads — DSP (Demand-Side Platform) v3 Column Map (Story 19.16, Task 7)
 *
 * Single source of truth for the 130 priority DSP metrics + 14 priority DSP
 * dimensions defined in Story 19.16 AC #5. Every entry uses the EXACT API
 * field name from `_bmad/bmm/data/integration-metrics/amazon-ads.md § Amazon
 * DSP — Metrics`. **One of the shipped field names is a wire-format typo
 * preserved verbatim**: `totalDetaiPageViewCPA14d` (missing `l`).
 * Correcting it would silently break the column lookup. A CI grep guardrail
 * (Task 13.6) ensures the corrected form NEVER appears in source code.
 *
 * **`playerTrailersClicks14d`** (extra `r` — Amazon's other documented wire-
 * format typo) is OUT-OF-SCOPE in 19.16 — one of the 13 click/view-split
 * video metrics deferred to 19.17 — the typo-observability log
 * `amazon_ads_dsp_typo_field_used` defends against it as a forward-compat
 * tripwire if Amazon ever returns the field unprompted.
 *
 * **Defensive contract:** if Amazon ever renames a column, the
 * `amazon_ads_column_drift_detected` warning fires at runtime AND the v2→v3
 * golden snapshot test fails in CI per AC #13.
 *
 * **DSP report-type naming convention:** Amazon's DSP API uses ALL_CAPS for
 * the wire-format `type` field (`"type": "CAMPAIGN"`), whereas SP/SB/SD use
 * lowerCamelCase reportTypeId values (`"reportTypeId": "spCampaigns"`). Our
 * internal `DspReportTypeId` union prefixes the value with `DSP_` for
 * grep-ability across the codebase; the prefix is stripped at body-build
 * time in `buildAmazonAdsReportRequest` via `AMAZON_ADS_DSP_TYPE_BY_REPORT_TYPE_ID`.
 *
 * Out of scope (ships in 19.17 with widget presets):
 *   - 145 funnel-event 5-field families (Accept14d / Decline14d / Application14d / etc.)
 *   - ~25 mashup collection-ad family
 *   - 35 brand-store-engagement levels 1-7 grid
 *   - AUDIENCE report type (schema not publicly documented)
 *   - TECHNICAL report type (newer surface)
 *   - AUDIO_VIDEO BETA report type
 */

import type { DimensionDefinition, MetricDefinition } from "../adapter";

// ============================================================================
// Report-type union (4 DSP report-type IDs supported in 19.16)
// ============================================================================

/**
 * Story 19.16 Task 1.5 / AC #1 — DSP report types in scope.
 * Story 19.16.1 AC #1 — extended to 7 DSP report types:
 *   - DSP_CAMPAIGN / DSP_INVENTORY / DSP_GEOGRAPHY / DSP_PRODUCT (carry-forward)
 *   - DSP_AUDIENCE — runtime-introspected (Code3-confirmed-floor: 7 metrics +
 *     2 dimensions; Amazon's docs page returns skeleton-only and Funnel.io
 *     OMITS this report-type from supported list; Singer.io claims support
 *     but README does NOT publish columns)
 *   - DSP_TECHNICAL — stable schema (5 metrics + 3 enum-extensible dimensions)
 *   - DSP_AUDIO_VIDEO — runtime-introspected BETA (Bright-Analytics-floor)
 *
 * **Frozen-identifier policy:** ADD-ONLY at the end. Reordering breaks
 * downstream switches that ship with `_exhaustive: never` exhaustiveness.
 */
export const DSP_REPORT_TYPE_IDS = [
  "DSP_CAMPAIGN",
  "DSP_INVENTORY",
  "DSP_GEOGRAPHY",
  "DSP_PRODUCT",
  "DSP_AUDIENCE",
  "DSP_TECHNICAL",
  "DSP_AUDIO_VIDEO",
] as const;

export type DspReportTypeId = (typeof DSP_REPORT_TYPE_IDS)[number];

// ============================================================================
// Metric column map (AC #5 — 120 priority metrics)
// ============================================================================

export type DspMetricDataType =
  | "Integer"
  | "Currency"
  | "Percentage"
  | "Ratio"
  | "Decimal"
  | "String";

export interface DspMetricColumnEntry {
  readonly key: string;
  readonly label: string;
  readonly reportTypeId: DspReportTypeId;
  readonly columnName: string;
  readonly dataType: DspMetricDataType;
  /**
   * Story 19.16 / Round 2 Pre-mortem D carry-forward — attribution window
   * in days. DSP uses 14d uniformly for ALL conversion-attributed metrics.
   * `undefined` for delivery-only fields (impressions, clicks, totalCost,
   * viewableImpressions) which have NO attribution window.
   */
  readonly attributionWindowDays?: 14;
  readonly compatibleWidgets: readonly string[];
  /** Story 31-31 — aggregation semantics, carried into the MetricDefinition projection. */
  readonly aggregationKind?: MetricDefinition["aggregationKind"];
  readonly ratioComponents?: MetricDefinition["ratioComponents"];
}

const KPI_LINE_BAR_TABLE = ["kpi", "line-chart", "bar-chart", "data-table"] as const;
const KPI_LINE_TABLE = ["kpi", "line-chart", "data-table"] as const;
const KPI_GAUGE_LINE = ["kpi", "gauge", "line-chart"] as const;
const TABLE_ONLY = ["data-table"] as const;

/**
 * The 120 priority DSP metrics ("DSP programmatic golden path") chosen via
 * Round 4 stakeholder QA-architect prioritization (Story 19.16 AC #5).
 *
 * Categories (verified May 2026 via integration-mapping directive § DSP +
 * Amazon Postman collection + Python Amazon Ad API SDK + Singer.io tap +
 * Funnel.io connector + Bright Analytics + Openbridge):
 *   - DSP Core Delivery (6): impressions / grossImpressions / invalid /
 *     invalidRate / measurable / measurableRate
 *   - DSP Viewability (2): viewableImpressions / viewabilityRate
 *   - DSP Clicks (6): clickThroughs / gross / invalid / invalidRate / CTR / eCPC
 *   - DSP Cost Structure (11): full fee decomposition
 *   - DSP Purchases-Total-14d (6): click+view split
 *   - DSP Sales & Units 14d (6): sales / totalSales / units / totalUnits / rates
 *   - DSP ROAS Family (4): ROAS14d / totalROAS14d / ntbROAS14d / combinedROAS
 *   - DSP NTB 14d (11): full new-to-brand decomposition
 *   - DSP Off-Amazon Conversions (13)
 *   - DSP Detail Page Views 14d (11) — INCLUDES `totalDetaiPageViewCPA14d` typo
 *   - DSP Add-to-Cart Family (5)
 *   - DSP ATC Short-Form (6)
 *   - DSP Add-to-Wishlist (5)
 *   - DSP Subscribe-and-Save (10)
 *   - DSP Brand Search Downstream (5)
 *   - DSP Mobile App First Start (5)
 *   - DSP Video — start + complete + quartile (14 of 27)
 *   - DSP Dimensional String Metadata (4) — across INVENTORY + GEOGRAPHY
 */
export const DSP_V3_COLUMN_MAP: Record<string, DspMetricColumnEntry> = {
  // ============== DSP Core Delivery (6) ==============
  impressions: {
    key: "impressions",
    label: "Impressions",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "impressions",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  grossImpressions: {
    key: "grossImpressions",
    label: "Gross Impressions",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "grossImpressions",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  invalidImpressions: {
    key: "invalidImpressions",
    label: "Invalid Impressions",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "invalidImpressions",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  invalidImpressionRate: {
    key: "invalidImpressionRate",
    label: "Invalid Impression Rate",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "invalidImpressionRate",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "invalidImpressions", denominator: "grossImpressions" },
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  measurableImpressions: {
    key: "measurableImpressions",
    label: "Measurable Impressions",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "measurableImpressions",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  measurableRate: {
    key: "measurableRate",
    label: "Measurable Rate",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "measurableRate",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "measurableImpressions", denominator: "impressions" },
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },

  // ============== DSP Viewability (2) ==============
  viewableImpressions: {
    key: "viewableImpressions",
    label: "Viewable Impressions",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "viewableImpressions",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  viewabilityRate: {
    key: "viewabilityRate",
    label: "Viewability Rate",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "viewabilityRate",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "viewableImpressions", denominator: "impressions" },
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },

  // ============== DSP Clicks (6) ==============
  clickThroughs: {
    key: "clickThroughs",
    label: "Click-Throughs",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "clickThroughs",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  grossClickThroughs: {
    key: "grossClickThroughs",
    label: "Gross Click-Throughs",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "grossClickThroughs",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  invalidClickThroughs: {
    key: "invalidClickThroughs",
    label: "Invalid Click-Throughs",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "invalidClickThroughs",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  invalidClickThroughsRate: {
    key: "invalidClickThroughsRate",
    label: "Invalid Click-Throughs Rate",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "invalidClickThroughsRate",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "invalidClickThroughs", denominator: "grossClickThroughs" },
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  CTR: {
    key: "CTR",
    label: "Click-Through Rate",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "CTR",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "clickThroughs", denominator: "impressions" },
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  eCPC: {
    key: "eCPC",
    label: "Effective Cost Per Click",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "eCPC",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "clickThroughs" },
    compatibleWidgets: [...KPI_LINE_TABLE],
  },

  // ============== DSP Cost Structure (11) ==============
  totalCost: {
    key: "totalCost",
    label: "Total Cost",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalCost",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  supplyCost: {
    key: "supplyCost",
    label: "Supply Cost",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "supplyCost",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  eCPM: {
    key: "eCPM",
    label: "Effective CPM",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "eCPM",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "impressions" },
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  totalFee: {
    key: "totalFee",
    label: "Total Fee",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalFee",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  agencyFee: {
    key: "agencyFee",
    label: "Agency Fee",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "agencyFee",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  amazonAudienceFee: {
    key: "amazonAudienceFee",
    label: "Amazon Audience Fee",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "amazonAudienceFee",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  amazonPlatformFee: {
    key: "amazonPlatformFee",
    label: "Amazon Platform Fee",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "amazonPlatformFee",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  "3PFees": {
    key: "3PFees",
    label: "3P Fees",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "3PFees",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  combinedECPP: {
    key: "combinedECPP",
    label: "Combined eCPP",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "combinedECPP",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "totalPurchases14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  combinedERPM: {
    key: "combinedERPM",
    label: "Combined eRPM",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "combinedERPM",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  totalECPP14d: {
    key: "totalECPP14d",
    label: "Total eCPP (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalECPP14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "totalPurchases14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },

  // ============== DSP Purchases-Total-14d (6) ==============
  totalPurchases14d: {
    key: "totalPurchases14d",
    label: "Total Purchases (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalPurchases14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  totalPurchasesClicks14d: {
    key: "totalPurchasesClicks14d",
    label: "Total Purchases — Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalPurchasesClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  totalPurchasesViews14d: {
    key: "totalPurchasesViews14d",
    label: "Total Purchases — Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalPurchasesViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  purchases14d: {
    key: "purchases14d",
    label: "Purchases (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "purchases14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  purchasesClicks14d: {
    key: "purchasesClicks14d",
    label: "Purchases — Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "purchasesClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  purchasesViews14d: {
    key: "purchasesViews14d",
    label: "Purchases — Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "purchasesViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },

  // ============== DSP Sales & Units 14d (6) ==============
  sales14d: {
    key: "sales14d",
    label: "Sales (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "sales14d",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  totalSales14d: {
    key: "totalSales14d",
    label: "Total Sales (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalSales14d",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  unitsSold14d: {
    key: "unitsSold14d",
    label: "Units Sold (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "unitsSold14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  totalUnitsSold14d: {
    key: "totalUnitsSold14d",
    label: "Total Units Sold (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalUnitsSold14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  purchaseRate14d: {
    key: "purchaseRate14d",
    label: "Purchase Rate (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "purchaseRate14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "purchases14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  totalPurchaseRate14d: {
    key: "totalPurchaseRate14d",
    label: "Total Purchase Rate (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalPurchaseRate14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalPurchases14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },

  // ============== DSP ROAS Family (4) ==============
  ROAS14d: {
    key: "ROAS14d",
    label: "ROAS (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "ROAS14d",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { numerator: "sales14d", denominator: "totalCost" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  totalROAS14d: {
    key: "totalROAS14d",
    label: "Total ROAS (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalROAS14d",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalSales14d", denominator: "totalCost" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  newToBrandROAS14d: {
    key: "newToBrandROAS14d",
    label: "New-To-Brand ROAS (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "newToBrandROAS14d",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { denominator: "totalCost" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  combinedROAS: {
    key: "combinedROAS",
    label: "Combined ROAS",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "combinedROAS",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { denominator: "totalCost" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },

  // ============== DSP New-To-Brand 14d (11) ==============
  newToBrandProductSales14d: {
    key: "newToBrandProductSales14d",
    label: "NTB Product Sales (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "newToBrandProductSales14d",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  newToBrandPurchases14d: {
    key: "newToBrandPurchases14d",
    label: "NTB Purchases (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "newToBrandPurchases14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  newToBrandPurchasesClicks14d: {
    key: "newToBrandPurchasesClicks14d",
    label: "NTB Purchases — Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "newToBrandPurchasesClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  newToBrandPurchasesViews14d: {
    key: "newToBrandPurchasesViews14d",
    label: "NTB Purchases — Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "newToBrandPurchasesViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  newToBrandUnitsSold14d: {
    key: "newToBrandUnitsSold14d",
    label: "NTB Units Sold (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "newToBrandUnitsSold14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  newToBrandPurchaseRate14d: {
    key: "newToBrandPurchaseRate14d",
    label: "NTB Purchase Rate (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "newToBrandPurchaseRate14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "newToBrandPurchases14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  newToBrandECPP14d: {
    key: "newToBrandECPP14d",
    label: "NTB eCPP (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "newToBrandECPP14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "newToBrandPurchases14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  newToBrandERPM14d: {
    key: "newToBrandERPM14d",
    label: "NTB eRPM (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "newToBrandERPM14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  percentOfPurchasesNewToBrand14d: {
    key: "percentOfPurchasesNewToBrand14d",
    label: "% of Purchases NTB (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "percentOfPurchasesNewToBrand14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "newToBrandPurchases14d", denominator: "purchases14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  totalNewToBrandProductSales14d: {
    key: "totalNewToBrandProductSales14d",
    label: "Total NTB Product Sales (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalNewToBrandProductSales14d",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  totalPercentOfPurchasesNewToBrand14d: {
    key: "totalPercentOfPurchasesNewToBrand14d",
    label: "Total % of Purchases NTB (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalPercentOfPurchasesNewToBrand14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "totalPurchases14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },

  // ============== DSP Off-Amazon Conversions (13) ==============
  offAmazonClicks14d: {
    key: "offAmazonClicks14d",
    label: "Off-Amazon Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "offAmazonClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  offAmazonConversions14d: {
    key: "offAmazonConversions14d",
    label: "Off-Amazon Conversions (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "offAmazonConversions14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  offAmazonCPA14d: {
    key: "offAmazonCPA14d",
    label: "Off-Amazon CPA (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "offAmazonCPA14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "offAmazonConversions14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  offAmazonCVR14d: {
    key: "offAmazonCVR14d",
    label: "Off-Amazon CVR (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "offAmazonCVR14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "offAmazonConversions14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  offAmazonECPP14d: {
    key: "offAmazonECPP14d",
    label: "Off-Amazon eCPP (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "offAmazonECPP14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "offAmazonPurchases14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  offAmazonERPM14d: {
    key: "offAmazonERPM14d",
    label: "Off-Amazon eRPM (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "offAmazonERPM14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  offAmazonProductSales14d: {
    key: "offAmazonProductSales14d",
    label: "Off-Amazon Product Sales (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "offAmazonProductSales14d",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  offAmazonPurchaseRate14d: {
    key: "offAmazonPurchaseRate14d",
    label: "Off-Amazon Purchase Rate (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "offAmazonPurchaseRate14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "offAmazonPurchases14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  offAmazonPurchases14d: {
    key: "offAmazonPurchases14d",
    label: "Off-Amazon Purchases (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "offAmazonPurchases14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  offAmazonPurchasesClicks14d: {
    key: "offAmazonPurchasesClicks14d",
    label: "Off-Amazon Purchases — Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "offAmazonPurchasesClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  offAmazonPurchasesViews14d: {
    key: "offAmazonPurchasesViews14d",
    label: "Off-Amazon Purchases — Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "offAmazonPurchasesViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  offAmazonROAS14d: {
    key: "offAmazonROAS14d",
    label: "Off-Amazon ROAS (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "offAmazonROAS14d",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { denominator: "totalCost" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  offAmazonUnitsSold14d: {
    key: "offAmazonUnitsSold14d",
    label: "Off-Amazon Units Sold (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "offAmazonUnitsSold14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },

  // ============== DSP Detail Page Views 14d (11) ==============
  // NOTE: `totalDetaiPageViewCPA14d` is Amazon's wire-format typo (missing
  // `l`). PRESERVED VERBATIM. CI grep guardrail asserts the corrected form
  // never appears in source code (Story 19.16 AC #13).
  dpv14d: {
    key: "dpv14d",
    label: "Detail Page Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "dpv14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  dpvClicks14d: {
    key: "dpvClicks14d",
    label: "DPV — Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "dpvClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  dpvViews14d: {
    key: "dpvViews14d",
    label: "DPV — Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "dpvViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  dpvr14d: {
    key: "dpvr14d",
    label: "DPVR (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "dpvr14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "dpv14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  eCPDPV14d: {
    key: "eCPDPV14d",
    label: "eCP-DPV (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "eCPDPV14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "dpv14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  eCPDVP14d: {
    key: "eCPDVP14d",
    label: "eCP-DVP (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "eCPDVP14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "dpv14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  totalDetailPageViews14d: {
    key: "totalDetailPageViews14d",
    label: "Total Detail Page Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalDetailPageViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  totalDetailPageClicks14d: {
    key: "totalDetailPageClicks14d",
    label: "Total Detail Page Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalDetailPageClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  totalDetailPageViewViews14d: {
    key: "totalDetailPageViewViews14d",
    label: "Total Detail Page View Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalDetailPageViewViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  totalDetailPageViewsCVR14d: {
    key: "totalDetailPageViewsCVR14d",
    label: "Total DPV CVR (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalDetailPageViewsCVR14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalDetailPageViews14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  // PRESERVED TYPO — Amazon's wire format is `totalDetaiPageViewCPA14d`
  // (missing `l`). Round 2 Pre-mortem B carry-forward: future PRs that
  // "fix" this typo break the column lookup; the grep guardrail in
  // Task 13.6 asserts the corrected form never appears in source code.
  totalDetaiPageViewCPA14d: {
    key: "totalDetaiPageViewCPA14d",
    label: "Total Detail Page View CPA (14d) [Amazon-typo preserved]",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalDetaiPageViewCPA14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "totalDetailPageViews14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },

  // ============== DSP Add-to-Cart Family (5) ==============
  totalAddToCart14d: {
    key: "totalAddToCart14d",
    label: "Total Add-to-Cart (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalAddToCart14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  totalAddToCartClicks14d: {
    key: "totalAddToCartClicks14d",
    label: "Total ATC — Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalAddToCartClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  totalAddToCartViews14d: {
    key: "totalAddToCartViews14d",
    label: "Total ATC — Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalAddToCartViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  totalAddToCartCPA14d: {
    key: "totalAddToCartCPA14d",
    label: "Total ATC CPA (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalAddToCartCPA14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "totalAddToCart14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  totalAddToCartCVR14d: {
    key: "totalAddToCartCVR14d",
    label: "Total ATC CVR (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalAddToCartCVR14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalAddToCart14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },

  // ============== DSP ATC Short-Form (6) ==============
  atc14d: {
    key: "atc14d",
    label: "ATC (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "atc14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  atcClicks14d: {
    key: "atcClicks14d",
    label: "ATC — Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "atcClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  atcViews14d: {
    key: "atcViews14d",
    label: "ATC — Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "atcViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  atcr14d: {
    key: "atcr14d",
    label: "ATCR (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "atcr14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "atc14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  eCPAtc14d: {
    key: "eCPAtc14d",
    label: "eCP-ATC (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "eCPAtc14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "atc14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  addedToShoppingCart14d: {
    key: "addedToShoppingCart14d",
    label: "Added to Shopping Cart (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "addedToShoppingCart14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },

  // ============== DSP Add-to-Wishlist (5) ==============
  addToWatchlist14d: {
    key: "addToWatchlist14d",
    label: "Add-to-Watchlist (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "addToWatchlist14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  addToWatchlistClicks14d: {
    key: "addToWatchlistClicks14d",
    label: "Add-to-Watchlist — Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "addToWatchlistClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  addToWatchlistViews14d: {
    key: "addToWatchlistViews14d",
    label: "Add-to-Watchlist — Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "addToWatchlistViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  addToWatchlistCPA14d: {
    key: "addToWatchlistCPA14d",
    label: "Add-to-Watchlist CPA (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "addToWatchlistCPA14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "addToWatchlist14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  addToWatchlistCVR14d: {
    key: "addToWatchlistCVR14d",
    label: "Add-to-Watchlist CVR (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "addToWatchlistCVR14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "addToWatchlist14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },

  // ============== DSP Subscribe-and-Save (10) ==============
  newSubscribeAndSave14d: {
    key: "newSubscribeAndSave14d",
    label: "New Subscribe-and-Save (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "newSubscribeAndSave14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  newSubscribeAndSaveClicks14d: {
    key: "newSubscribeAndSaveClicks14d",
    label: "New SnS — Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "newSubscribeAndSaveClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  newSubscribeAndSaveViews14d: {
    key: "newSubscribeAndSaveViews14d",
    label: "New SnS — Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "newSubscribeAndSaveViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  newSubscribeAndSaveRate14d: {
    key: "newSubscribeAndSaveRate14d",
    label: "New SnS Rate (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "newSubscribeAndSaveRate14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "newSubscribeAndSave14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  eCPnewSubscribeAndSave14d: {
    key: "eCPnewSubscribeAndSave14d",
    label: "eCP New SnS (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "eCPnewSubscribeAndSave14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "newSubscribeAndSave14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  totalSubscribeAndSaveSubscriptions14d: {
    key: "totalSubscribeAndSaveSubscriptions14d",
    label: "Total SnS Subscriptions (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalSubscribeAndSaveSubscriptions14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  totalSubscribeAndSaveSubscriptionClicks14d: {
    key: "totalSubscribeAndSaveSubscriptionClicks14d",
    label: "Total SnS — Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalSubscribeAndSaveSubscriptionClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  totalSubscribeAndSaveSubscriptionViews14d: {
    key: "totalSubscribeAndSaveSubscriptionViews14d",
    label: "Total SnS — Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalSubscribeAndSaveSubscriptionViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  totalSubscribeAndSaveSubscriptionCPA14d: {
    key: "totalSubscribeAndSaveSubscriptionCPA14d",
    label: "Total SnS CPA (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalSubscribeAndSaveSubscriptionCPA14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "totalSubscribeAndSaveSubscriptions14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  totalSubscribeAndSaveSubscriptionCVR14d: {
    key: "totalSubscribeAndSaveSubscriptionCVR14d",
    label: "Total SnS CVR (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "totalSubscribeAndSaveSubscriptionCVR14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalSubscribeAndSaveSubscriptions14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },

  // ============== DSP Brand Search Downstream (5) ==============
  brandSearch14d: {
    key: "brandSearch14d",
    label: "Brand Search (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "brandSearch14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  brandSearchClicks14d: {
    key: "brandSearchClicks14d",
    label: "Brand Search — Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "brandSearchClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  brandSearchViews14d: {
    key: "brandSearchViews14d",
    label: "Brand Search — Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "brandSearchViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  brandSearchRate14d: {
    key: "brandSearchRate14d",
    label: "Brand Search Rate (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "brandSearchRate14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "brandSearch14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  brandSearchCPA14d: {
    key: "brandSearchCPA14d",
    label: "Brand Search CPA (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "brandSearchCPA14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "brandSearch14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },

  // ============== DSP Mobile App First Start (5) ==============
  mobileAppFirstStarts14d: {
    key: "mobileAppFirstStarts14d",
    label: "Mobile App First Starts (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mobileAppFirstStarts14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  mobileAppFirstStartClicks14d: {
    key: "mobileAppFirstStartClicks14d",
    label: "Mobile App First Start — Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mobileAppFirstStartClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  mobileAppFirstStartViews14d: {
    key: "mobileAppFirstStartViews14d",
    label: "Mobile App First Start — Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mobileAppFirstStartViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  mobileAppFirstStartsCPA14d: {
    key: "mobileAppFirstStartsCPA14d",
    label: "Mobile App First Start CPA (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mobileAppFirstStartsCPA14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "mobileAppFirstStarts14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  mobileAppFirstStartCVR14d: {
    key: "mobileAppFirstStartCVR14d",
    label: "Mobile App First Start CVR (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "mobileAppFirstStartCVR14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "mobileAppFirstStarts14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },

  // ============== DSP Video — start + complete + quartile (14) ==============
  videoStart: {
    key: "videoStart",
    label: "Video Start",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoStart",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  videoStarted: {
    key: "videoStarted",
    label: "Video Started",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoStarted",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  videoFirstQuartile: {
    key: "videoFirstQuartile",
    label: "Video First Quartile",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoFirstQuartile",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  videoMidpoint: {
    key: "videoMidpoint",
    label: "Video Midpoint",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoMidpoint",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  videoThirdQuartile: {
    key: "videoThirdQuartile",
    label: "Video Third Quartile",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoThirdQuartile",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  videoComplete: {
    key: "videoComplete",
    label: "Video Complete",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoComplete",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  videoCompleted: {
    key: "videoCompleted",
    label: "Video Completed",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoCompleted",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  videoCompletionRate: {
    key: "videoCompletionRate",
    label: "Video Completion Rate",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoCompletionRate",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "videoCompleted", denominator: "videoStarted" },
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  videoMute: {
    key: "videoMute",
    label: "Video Mute",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoMute",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  videoUnmute: {
    key: "videoUnmute",
    label: "Video Unmute",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoUnmute",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  videoPause: {
    key: "videoPause",
    label: "Video Pause",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoPause",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  videoResume: {
    key: "videoResume",
    label: "Video Resume",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoResume",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  videoEndClicks: {
    key: "videoEndClicks",
    label: "Video End Clicks",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoEndClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  playTrailers14d: {
    key: "playTrailers14d",
    label: "Play Trailers (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "playTrailers14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },

  // ==========================================================================
  // Story 19.16.1 AC #4 — DSP Video click/view-split metrics (13 NEW)
  // Extends 19.16's 14-of-27 video set to 27-of-27 = 100% of Amazon's
  // published DSP video catalogue. Includes the SECOND preserved Amazon
  // wire-format typo: `playerTrailersClicks14d` (extra `r` — `player` not
  // `play`). DO NOT correct — Amazon's wire format is the source of truth.
  // ==========================================================================
  videoStartedClicks: {
    key: "videoStartedClicks",
    label: "Video Started — Clicks",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoStartedClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  videoStartedViews: {
    key: "videoStartedViews",
    label: "Video Started — Views",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoStartedViews",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  videoStartedCPA: {
    key: "videoStartedCPA",
    label: "Video Started — CPA",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoStartedCPA",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "videoStarted" },
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  videoStartedCVR: {
    key: "videoStartedCVR",
    label: "Video Started — CVR",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoStartedCVR",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "videoStarted", denominator: "impressions" },
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  videoCompletedClicks: {
    key: "videoCompletedClicks",
    label: "Video Completed — Clicks",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoCompletedClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  videoCompletedViews: {
    key: "videoCompletedViews",
    label: "Video Completed — Views",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoCompletedViews",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  videoCompletedCPA: {
    key: "videoCompletedCPA",
    label: "Video Completed — CPA",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoCompletedCPA",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "videoCompleted" },
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  videoCompletedCVR: {
    key: "videoCompletedCVR",
    label: "Video Completed — CVR",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoCompletedCVR",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "videoCompleted", denominator: "impressions" },
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  videoDownloads14d: {
    key: "videoDownloads14d",
    label: "Video Downloads (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoDownloads14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  videoDownloadsClicks14d: {
    key: "videoDownloadsClicks14d",
    label: "Video Downloads — Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoDownloadsClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  videoDownloadsViews14d: {
    key: "videoDownloadsViews14d",
    label: "Video Downloads — Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoDownloadsViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  videoDownloadsCPA14d: {
    key: "videoDownloadsCPA14d",
    label: "Video Downloads — CPA (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoDownloadsCPA14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "videoDownloads14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  videoStreams14d: {
    key: "videoStreams14d",
    label: "Video Streams (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoStreams14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  // **AMAZON-TYPO PRESERVATION (Story 19.16.1 AC #4)** — Amazon's actual wire
  // format is `playerTrailersClicks14d` (extra `r` — `player...` not
  // `play...`). The non-typo'd `playTrailers14d` (without `Clicks14d`) IS a
  // separate, correctly-named metric on Amazon's wire (line ~1261); the
  // 14d-Clicks variant gets the `er` typo. DO NOT correct.
  // CI guardrail (`dsp-column-map.test.ts`): `playTrailersClicks14d`
  // (missing `er` — the would-be-corrected form) MUST NOT appear as a
  // `key` or `columnName` anywhere in DSP_V3_COLUMN_MAP. The
  // `amazon_ads_dsp_typo_field_used` log in `dsp-fetch-metrics.ts` fires on
  // first observation of either typo (`totalDetaiPageViewCPA14d` from 19.16
  // or `playerTrailersClicks14d` from 19.16.1).
  playerTrailersClicks14d: {
    key: "playerTrailersClicks14d",
    label: "Player Trailers — Clicks (14d) [Amazon-typo-preserved]",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "playerTrailersClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },

  // ============== DSP Dimensional String Metadata (4) ==============
  // String-typed metadata exposed as metrics for the data-table widget.
  advertiserCountry: {
    key: "advertiserCountry",
    label: "Advertiser Country",
    reportTypeId: "DSP_GEOGRAPHY",
    columnName: "advertiserCountry",
    dataType: "String",
    compatibleWidgets: [...TABLE_ONLY],
  },
  advertiserTimezone: {
    key: "advertiserTimezone",
    label: "Advertiser Timezone",
    reportTypeId: "DSP_GEOGRAPHY",
    columnName: "advertiserTimezone",
    dataType: "String",
    compatibleWidgets: [...TABLE_ONLY],
  },
  siteName: {
    key: "siteName",
    label: "Site Name",
    reportTypeId: "DSP_INVENTORY",
    columnName: "siteName",
    dataType: "String",
    compatibleWidgets: [...TABLE_ONLY],
  },
  placementName: {
    key: "placementName",
    label: "Placement Name",
    reportTypeId: "DSP_INVENTORY",
    columnName: "placementName",
    dataType: "String",
    compatibleWidgets: [...TABLE_ONLY],
  },

  // ==========================================================================
  // Story 35.7 AC #4 — DSP PRPV (5) + Video stream/download/trailer rates (6).
  // All 11 live on the existing DSP_CAMPAIGN report type — no new DSP
  // report-type IDs introduced.
  //
  // **Amazon-typo preservation invariant** (Round 5 Q2 carry-forward, 19.16):
  // `pRPV14d` (lowercase `p`, uppercase `RPV`) is Amazon's actual wire-format
  // field name per the DSP Postman collection — "fixing" to `prpv14d` or
  // `PRPV14d` silently breaks the column lookup. The CI grep guardrail
  // forbids the corrected forms in source code.
  // ==========================================================================
  // DSP PRPV — Product Review Page Visits (5 — DSP_CAMPAIGN).
  // User visited the product reviews tab on a Detail Page that received a DSP
  // impression in the prior 14 days. Different intent signal from basic DPV —
  // high signal for high-consideration purchases like consumer electronics.
  pRPV14d: {
    key: "pRPV14d",
    label: "PRPV (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "pRPV14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  pRPVClicks14d: {
    key: "pRPVClicks14d",
    label: "PRPV Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "pRPVClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  pRPVViews14d: {
    key: "pRPVViews14d",
    label: "PRPV Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "pRPVViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  pRPVr14d: {
    key: "pRPVr14d",
    label: "PRPV Rate (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "pRPVr14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "pRPV14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  eCPPRPV14d: {
    key: "eCPPRPV14d",
    label: "eCP PRPV (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "eCPPRPV14d",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalCost", denominator: "pRPV14d" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  // DSP Video stream/download rates (4 — DSP_CAMPAIGN). Paired with the
  // 19.16.1-shipped `videoDownloads14d` / `videoStreams14d` core counters.
  videoDownloadRate14d: {
    key: "videoDownloadRate14d",
    label: "Video Download Rate (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoDownloadRate14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "videoDownloads14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  videoStreamsClicks14d: {
    key: "videoStreamsClicks14d",
    label: "Video Streams Clicks (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoStreamsClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  videoStreamsViews14d: {
    key: "videoStreamsViews14d",
    label: "Video Streams Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoStreamsViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  videoStreamsRate14d: {
    key: "videoStreamsRate14d",
    label: "Video Streams Rate (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "videoStreamsRate14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "videoStreams14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
  // DSP Trailer engagement (2 — DSP_CAMPAIGN).
  playTrailersViews14d: {
    key: "playTrailersViews14d",
    label: "Play Trailers Views (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "playTrailersViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  playTrailerRate14d: {
    key: "playTrailerRate14d",
    label: "Play Trailer Rate (14d)",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "playTrailerRate14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "playTrailers14d", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_GAUGE_LINE],
  },
};

// ============================================================================
// Dimension column map (AC #1)
// ============================================================================

export interface DspDimensionColumnEntry {
  readonly key: string;
  readonly label: string;
  readonly reportTypeId: DspReportTypeId;
  readonly columnName: string;
  readonly groupByValue: string;
}

/**
 * 13 NEW DSP-specific dimensions across 4 report types (AC #1):
 *   - DSP_CAMPAIGN: ORDER, LINE_ITEM, CREATIVE
 *   - DSP_INVENTORY: siteName, placementName, dealId
 *   - DSP_GEOGRAPHY: country, region, city, marketAreaCode, postalCode
 *   - DSP_PRODUCT: asin, brandName, productCategory
 */
export const DSP_V3_DIMENSION_MAP: Record<string, DspDimensionColumnEntry> = {
  // DSP_CAMPAIGN dimensions
  ORDER: {
    key: "ORDER",
    label: "Order",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "ORDER",
    groupByValue: "ORDER",
  },
  LINE_ITEM: {
    key: "LINE_ITEM",
    label: "Line Item",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "LINE_ITEM",
    groupByValue: "LINE_ITEM",
  },
  CREATIVE: {
    key: "CREATIVE",
    label: "Creative",
    reportTypeId: "DSP_CAMPAIGN",
    columnName: "CREATIVE",
    groupByValue: "CREATIVE",
  },

  // DSP_INVENTORY dimensions
  siteName: {
    key: "siteName",
    label: "Site Name",
    reportTypeId: "DSP_INVENTORY",
    columnName: "siteName",
    groupByValue: "siteName",
  },
  placementName: {
    key: "placementName",
    label: "Placement Name",
    reportTypeId: "DSP_INVENTORY",
    columnName: "placementName",
    groupByValue: "placementName",
  },
  dealId: {
    key: "dealId",
    label: "Deal ID",
    reportTypeId: "DSP_INVENTORY",
    columnName: "dealId",
    groupByValue: "dealId",
  },

  // DSP_GEOGRAPHY dimensions
  country: {
    key: "country",
    label: "Country",
    reportTypeId: "DSP_GEOGRAPHY",
    columnName: "country",
    groupByValue: "country",
  },
  region: {
    key: "region",
    label: "Region",
    reportTypeId: "DSP_GEOGRAPHY",
    columnName: "region",
    groupByValue: "region",
  },
  city: {
    key: "city",
    label: "City",
    reportTypeId: "DSP_GEOGRAPHY",
    columnName: "city",
    groupByValue: "city",
  },
  marketAreaCode: {
    key: "marketAreaCode",
    label: "Market Area Code",
    reportTypeId: "DSP_GEOGRAPHY",
    columnName: "marketAreaCode",
    groupByValue: "marketAreaCode",
  },
  postalCode: {
    key: "postalCode",
    label: "Postal Code",
    reportTypeId: "DSP_GEOGRAPHY",
    columnName: "postalCode",
    groupByValue: "postalCode",
  },

  // DSP_PRODUCT dimensions
  asin: {
    key: "asin",
    label: "ASIN",
    reportTypeId: "DSP_PRODUCT",
    columnName: "asin",
    groupByValue: "asin",
  },
  brandName: {
    key: "brandName",
    label: "Brand Name",
    reportTypeId: "DSP_PRODUCT",
    columnName: "brandName",
    groupByValue: "brandName",
  },
  productCategory: {
    key: "productCategory",
    label: "Product Category",
    reportTypeId: "DSP_PRODUCT",
    columnName: "productCategory",
    groupByValue: "productCategory",
  },

  // Story 19.16.1 — DSP_AUDIENCE dimensions (Code3-confirmed-floor + extras)
  segmentId: {
    key: "segmentId",
    label: "Segment ID",
    reportTypeId: "DSP_AUDIENCE",
    columnName: "segmentId",
    groupByValue: "segmentId",
  },
  segmentName: {
    key: "segmentName",
    label: "Segment Name",
    reportTypeId: "DSP_AUDIENCE",
    columnName: "segmentName",
    groupByValue: "segmentName",
  },
  segmentType: {
    key: "segmentType",
    label: "Segment Type",
    reportTypeId: "DSP_AUDIENCE",
    columnName: "segmentType",
    groupByValue: "segmentType",
  },
  matchType: {
    key: "matchType",
    label: "Match Type",
    reportTypeId: "DSP_AUDIENCE",
    columnName: "matchType",
    groupByValue: "matchType",
  },
  audienceOverlap: {
    key: "audienceOverlap",
    label: "Audience Overlap",
    reportTypeId: "DSP_AUDIENCE",
    columnName: "audienceOverlap",
    groupByValue: "audienceOverlap",
  },
  audienceSize: {
    key: "audienceSize",
    label: "Audience Size",
    reportTypeId: "DSP_AUDIENCE",
    columnName: "audienceSize",
    groupByValue: "audienceSize",
  },
  affinityIndex: {
    key: "affinityIndex",
    label: "Affinity Index",
    reportTypeId: "DSP_AUDIENCE",
    columnName: "affinityIndex",
    groupByValue: "affinityIndex",
  },

  // Story 19.16.1 — DSP_TECHNICAL dimensions
  device: {
    key: "device",
    label: "Device",
    reportTypeId: "DSP_TECHNICAL",
    columnName: "device",
    groupByValue: "device",
  },
  environment: {
    key: "environment",
    label: "Environment",
    reportTypeId: "DSP_TECHNICAL",
    columnName: "environment",
    groupByValue: "environment",
  },
  measurementSource: {
    key: "measurementSource",
    label: "Measurement Source",
    reportTypeId: "DSP_TECHNICAL",
    columnName: "measurementSource",
    groupByValue: "measurementSource",
  },

  // Story 19.16.1 — DSP_AUDIO_VIDEO BETA dimensions
  creativeType: {
    key: "creativeType",
    label: "Creative Type",
    reportTypeId: "DSP_AUDIO_VIDEO",
    columnName: "creativeType",
    groupByValue: "creativeType",
  },
  audioFormat: {
    key: "audioFormat",
    label: "Audio Format",
    reportTypeId: "DSP_AUDIO_VIDEO",
    columnName: "audioFormat",
    groupByValue: "audioFormat",
  },
  videoFormat: {
    key: "videoFormat",
    label: "Video Format",
    reportTypeId: "DSP_AUDIO_VIDEO",
    columnName: "videoFormat",
    groupByValue: "videoFormat",
  },
};

// ============================================================================
// Public arrays (consumed by getAvailableMetrics / getAvailableDimensions)
// ============================================================================

/** AC #5 — exactly 120 priority DSP metrics. */
export const AMAZON_ADS_DSP_METRICS: MetricDefinition[] = Object.values(
  DSP_V3_COLUMN_MAP
).map((m) => ({
  key: m.key,
  label: m.label,
  type:
    m.dataType === "Currency"
      ? "currency"
      : m.dataType === "Percentage"
        ? "percentage"
        : "number",
  category: "Amazon Ads DSP",
  dataType:
    m.dataType === "String" ? "Enum" : m.dataType === "Decimal" ? "Decimal" : m.dataType,
  // Story 31-31 — carry aggregation metadata through the projection.
  ...(m.aggregationKind !== undefined ? { aggregationKind: m.aggregationKind } : {}),
  ...(m.ratioComponents !== undefined ? { ratioComponents: m.ratioComponents } : {}),
  compatibleWidgets: [...m.compatibleWidgets],
  apiField: m.columnName,
}));

/** AC #1 — DSP-only dimensions; extended in Story 19.16.1 to 26 dims (13 + 13 new). */
export const AMAZON_ADS_DSP_DIMENSIONS: DimensionDefinition[] = Object.values(
  DSP_V3_DIMENSION_MAP
).map((d) => ({
  key: d.key,
  label: d.label,
  type:
    d.reportTypeId === "DSP_GEOGRAPHY"
      ? "Geo"
      : d.reportTypeId === "DSP_PRODUCT"
        ? "Product"
        : d.reportTypeId === "DSP_INVENTORY"
          ? "Delivery"
          : d.reportTypeId === "DSP_AUDIENCE"
            ? "Audience"
            : d.reportTypeId === "DSP_TECHNICAL"
              ? "Tech"
              : d.reportTypeId === "DSP_AUDIO_VIDEO"
                ? "Content"
                : "Structure",
}));

// ============================================================================
// Pure helper: pick report type from dimensions (AC #1)
// ============================================================================

export type PickDspReportTypeResult =
  | {
      readonly ok: true;
      readonly reportTypeId: DspReportTypeId;
      readonly warnings: string[];
    }
  | {
      readonly ok: false;
      readonly error:
        | "unsupported_dimension_combination"
        | "ad_product_dimension_mismatch";
      readonly message: string;
    };

const DSP_CAMPAIGN_DIMS = new Set(["ORDER", "LINE_ITEM", "CREATIVE"]);
const DSP_INVENTORY_DIMS = new Set(["siteName", "placementName", "dealId"]);
const DSP_GEOGRAPHY_DIMS = new Set([
  "country",
  "region",
  "city",
  "marketAreaCode",
  "postalCode",
]);
const DSP_PRODUCT_DIMS = new Set(["asin", "brandName", "productCategory"]);
/**
 * Story 19.16.1 AC #1 / #8 — AUDIENCE dimensions. Code3 May 2026 confirms
 * the production agency-facing floor: `audienceSize` (enum SMALL/MEDIUM/LARGE)
 * + `affinityIndex` (numeric > 0). Additional `segmentId` / `segmentName` /
 * `segmentType` / `matchType` / `audienceOverlap` are runtime-introspected
 * per tenant on first fetch.
 */
const DSP_AUDIENCE_DIMS = new Set([
  "segmentId",
  "segmentName",
  "segmentType",
  "matchType",
  "audienceOverlap",
  "audienceSize",
  "affinityIndex",
]);
/**
 * Story 19.16.1 AC #1 / #9 — TECHNICAL dimensions (stable schema, 2022+).
 * Enum-typed but extensibility-tolerant per Round 2 Pre-mortem D.
 */
const DSP_TECHNICAL_DIMS = new Set(["device", "environment", "measurementSource"]);
/**
 * Story 19.16.1 AC #1 / #10 — AUDIO_VIDEO BETA dimensions. Bright Analytics
 * V3 confirmed floor; runtime-introspected schema otherwise.
 */
const DSP_AUDIO_VIDEO_DIMS = new Set(["creativeType", "audioFormat", "videoFormat"]);

/**
 * Story 19.16 Task 2.2 / AC #1 — deterministic pure function that maps a
 * requested dimension set to the correct DSP report type.
 *
 * Rules:
 *   1. dim ⊆ {ORDER, LINE_ITEM, CREATIVE} → DSP_CAMPAIGN
 *   2. siteName | placementName | dealId ∈ dim → DSP_INVENTORY
 *   3. country | region | city | marketAreaCode | postalCode ∈ dim → DSP_GEOGRAPHY
 *   4. asin | brandName | productCategory ∈ dim → DSP_PRODUCT
 *   5. Cross-report-type mixing (e.g. asin + country) → unsupported_dimension_combination
 *   6. SP/SB/SD-only dimensions (purchasedAsin / promotedAsin / etc.) →
 *      ad_product_dimension_mismatch (Round 3 Vuln 4 carry-forward)
 *
 * Empty dimension array defaults to DSP_CAMPAIGN (most common request).
 */
export function pickDspReportType(
  dimensions: ReadonlyArray<string>
): PickDspReportTypeResult {
  const set = new Set(dimensions);
  const warnings: string[] = [];

  // Sponsored-ads-only dimensions that must NEVER reach DSP (Round 3 Vuln 4).
  const SPONSORED_ADS_ONLY_DIMS = new Set([
    "campaignId",
    "campaignName",
    "campaignStatus",
    "adGroupId",
    "adGroupName",
    "advertisedAsin",
    "advertisedSku",
    "purchasedAsin",
    "promotedAsin",
    "promotedSku",
    "targetingExpression",
    "adId",
  ]);

  const buckets = new Set<DspReportTypeId>();
  const unknown: string[] = [];

  for (const d of set) {
    if (SPONSORED_ADS_ONLY_DIMS.has(d)) {
      return {
        ok: false,
        error: "ad_product_dimension_mismatch",
        message: `Dimension ${d} is a Sponsored Ads dimension and cannot be used with DSP`,
      };
    }
    if (DSP_CAMPAIGN_DIMS.has(d)) {
      buckets.add("DSP_CAMPAIGN");
      continue;
    }
    if (DSP_INVENTORY_DIMS.has(d)) {
      buckets.add("DSP_INVENTORY");
      continue;
    }
    if (DSP_GEOGRAPHY_DIMS.has(d)) {
      buckets.add("DSP_GEOGRAPHY");
      continue;
    }
    if (DSP_PRODUCT_DIMS.has(d)) {
      buckets.add("DSP_PRODUCT");
      continue;
    }
    if (DSP_AUDIENCE_DIMS.has(d)) {
      buckets.add("DSP_AUDIENCE");
      continue;
    }
    if (DSP_TECHNICAL_DIMS.has(d)) {
      buckets.add("DSP_TECHNICAL");
      continue;
    }
    if (DSP_AUDIO_VIDEO_DIMS.has(d)) {
      buckets.add("DSP_AUDIO_VIDEO");
      continue;
    }
    unknown.push(d);
  }

  if (unknown.length > 0) {
    return {
      ok: false,
      error: "unsupported_dimension_combination",
      message: `Unknown dimension(s) for DSP: ${unknown.join(", ")}`,
    };
  }

  if (buckets.size > 1) {
    return {
      ok: false,
      error: "unsupported_dimension_combination",
      message: `Dimensions span multiple DSP report types (${[...buckets].join(", ")}). Pick one report-type axis per request.`,
    };
  }

  if (buckets.size === 1) {
    return { ok: true, reportTypeId: [...buckets][0]!, warnings };
  }

  // Empty dimension array → default to DSP_CAMPAIGN.
  return { ok: true, reportTypeId: "DSP_CAMPAIGN", warnings };
}

/**
 * Story 19.16.1 — registry of additional DSP column-map sources beyond
 * `DSP_V3_COLUMN_MAP`. Wired here as a parameter rather than imported at the
 * top of the file to avoid circular imports between dsp-column-map and the
 * sub-maps. Callers (`buildAmazonAdsReportRequest`) inject the registry; the
 * default `buildDspColumnList` consults the legacy sole map for back-compat.
 *
 * Each entry maps `metricKey → reportTypeId`. Multiple registries combine
 * additively when looking up a key.
 */
export interface DspExtendedColumnSource {
  readonly metricKey: string;
  readonly reportTypeId: DspReportTypeId;
  readonly columnName: string;
}

let extendedColumnRegistry: ReadonlyArray<DspExtendedColumnSource> = [];

/**
 * Story 19.16.1 — register additional column-map sources for
 * `buildDspColumnList` to consult. Called once at module-load by the
 * aggregator (`amazon-ads.ts`) with funnel/mashup/technical/AUDIENCE-floor/
 * AUDIO_VIDEO-floor entries. Idempotent — re-registration replaces.
 */
export function registerDspExtendedColumns(
  sources: ReadonlyArray<DspExtendedColumnSource>
): void {
  extendedColumnRegistry = sources;
}

/**
 * Returns the columns to request for a given (reportTypeId, requestedMetrics,
 * requestedDimensions) combo. Drops any metric/dimension that doesn't belong
 * to the chosen report type.
 *
 * Story 19.16.1 — consults extended sources (funnel/mashup/technical/AUDIENCE-
 * floor/AUDIO_VIDEO-floor) registered via `registerDspExtendedColumns` after
 * the legacy `DSP_V3_COLUMN_MAP` lookup misses.
 */
export function buildDspColumnList(
  reportTypeId: DspReportTypeId,
  requestedMetricKeys: ReadonlyArray<string>,
  requestedDimensionKeys: ReadonlyArray<string>
): { columns: string[]; warnings: string[] } {
  const warnings: string[] = [];
  const columns = new Set<string>();

  for (const dk of requestedDimensionKeys) {
    const d = DSP_V3_DIMENSION_MAP[dk];
    if (!d) {
      warnings.push(`Unknown DSP dimension key dropped: ${dk}`);
      continue;
    }
    if (d.reportTypeId !== reportTypeId) {
      warnings.push(
        `Dimension ${dk} valid on ${d.reportTypeId}, dropped for ${reportTypeId}`
      );
      continue;
    }
    columns.add(d.columnName);
  }

  for (const mk of requestedMetricKeys) {
    const m = DSP_V3_COLUMN_MAP[mk];
    if (m) {
      if (m.reportTypeId !== reportTypeId) {
        warnings.push(
          `Metric ${mk} valid on ${m.reportTypeId}, dropped for ${reportTypeId}`
        );
        continue;
      }
      columns.add(m.columnName);
      continue;
    }
    // Story 19.16.1 — fall through to extended registry (funnel/mashup/
    // technical/AUDIENCE-floor/AUDIO_VIDEO-floor).
    const extended = extendedColumnRegistry.find((e) => e.metricKey === mk);
    if (extended) {
      if (extended.reportTypeId !== reportTypeId) {
        warnings.push(
          `Metric ${mk} valid on ${extended.reportTypeId}, dropped for ${reportTypeId}`
        );
        continue;
      }
      columns.add(extended.columnName);
      continue;
    }
    warnings.push(`Unknown DSP metric key dropped: ${mk}`);
  }

  return { columns: [...columns], warnings };
}

/** Returns the deduped `dimensions` array for the DSP request body. */
export function buildDspGroupByList(
  reportTypeId: DspReportTypeId,
  requestedDimensionKeys: ReadonlyArray<string>
): string[] {
  const out = new Set<string>();
  for (const dk of requestedDimensionKeys) {
    const d = DSP_V3_DIMENSION_MAP[dk];
    if (!d) continue;
    if (d.reportTypeId !== reportTypeId) continue;
    out.add(d.groupByValue);
  }
  if (out.size === 0) {
    // Default group-by per report type (matches Amazon's Postman defaults).
    if (reportTypeId === "DSP_CAMPAIGN") out.add("ORDER");
    else if (reportTypeId === "DSP_INVENTORY") out.add("siteName");
    else if (reportTypeId === "DSP_GEOGRAPHY") out.add("country");
    else if (reportTypeId === "DSP_PRODUCT") out.add("asin");
    else if (reportTypeId === "DSP_AUDIENCE") out.add("segmentId");
    else if (reportTypeId === "DSP_TECHNICAL") out.add("device");
    else if (reportTypeId === "DSP_AUDIO_VIDEO") out.add("creativeType");
  }
  return [...out];
}

/** Returns the list of supported DSP report-type IDs (back-compat helper). */
export function getDspReportTypeIds(): ReadonlyArray<DspReportTypeId> {
  return DSP_REPORT_TYPE_IDS;
}
